import {
  type SeriesPoint,
  scaleBand,
  scaleLinear,
  scaleOrdinal,
  stack,
} from 'd3';
import { clamp, min } from 'lodash';

import { useResizeObserver } from '@revelio/core';

import { ReplotChartNames } from '../../constants';
import { PatternDiagonalStripe } from '../../svg-patterns/patterns';
import { type CommonPlotProps, FormatType } from '../../types';
import {
  getFormatter,
  getMaxTextWidth,
  getOtherColor,
  plotColors,
} from '../../utilities';
import { createCombinedLegendFormatter } from '../../utilities/create-combined-legend-formatter';
import { plotTestId } from '../../utilities/plotTestId';
import { AxisLabel } from '../axis/axis-label';
import { Legend } from '../legend/legend';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import styles from './stacked-bar-chart-horizontal.module.css';
import { StackedBarTooltip } from './stacked-bar-tooltip';

const Dimensions = {
  BarLeftPadding: 5,
  MaxBarHeight: 40,
  MinBarHeight: 10,
  BarYSpacingFactor: 0.2,
  BarXSpacing: 2,
  LegendHeight: 42,
};

export type StackedBarData = {
  label: string;
  segments: Record<string, number>;
};

// Internal type with index for distinguishing duplicates
type StackedBarDataIndexed = StackedBarData & { _index: number };

export type StackedBarChartHorizontalProps = {
  data: StackedBarData[];
  format?: FormatType;
  colorOverride?: Record<string, string>;
  colorPalette?: string[];
  hideLegend?: boolean;
  formatLegendLabel?: (label: string) => string;
  maxLabelWidth?: number;
  maxLegendLabelWidth?: number;
  segmentOrder?: string[];
} & CommonPlotProps;

export const StackedBarChartHorizontal = ({
  data = [],
  loading = false,
  format = FormatType.INTEGER,
  colorOverride,
  colorPalette = plotColors,
  hideLegend,
  maxLabelWidth = 75,
  formatLegendLabel = (label) => label,
  maxLegendLabelWidth,
  segmentOrder,
}: StackedBarChartHorizontalProps) => {
  const { containerRef, width, height } = useResizeObserver();

  // Create combined formatter for legend labels (custom formatting + truncation)
  const combinedLegendFormatter = createCombinedLegendFormatter(
    formatLegendLabel,
    maxLegendLabelWidth
  );

  /* ======== Handle duplicate labels ======== */
  const dataIndexed: StackedBarDataIndexed[] = data.map((bar, idx) => ({
    ...bar,
    _index: idx,
  }));

  const labelCounts = new Map<string, number>();
  const displayLabels = dataIndexed.map((bar) => {
    const count = labelCounts.get(bar.label) ?? 0;
    labelCounts.set(bar.label, count + 1);
    return count === 0 ? bar.label : `${bar.label} (${count + 1})`;
  });

  const yDomain = dataIndexed.map((d) => d._index.toString());

  /* Plot dimensions */
  const labelWidth = min([
    getMaxTextWidth({
      texts: displayLabels,
      fontSize: 11,
      fontFamily: '"Source Sans Pro", sans-serif',
    }),
    maxLabelWidth,
  ]) as number;
  const legendHeight = hideLegend ? 0 : Dimensions.LegendHeight;
  const minHeight = data.length * Dimensions.MinBarHeight;
  const maxHeight = data.length * Dimensions.MaxBarHeight;
  const plotHeight = clamp(height - legendHeight, minHeight, maxHeight);
  const plotWidth = Math.max(0, width - labelWidth - Dimensions.BarLeftPadding);

  const isPlotSizeValid = plotWidth > 0 && plotHeight > 0;
  const shouldHideLegend = hideLegend || legendHeight + plotHeight > height;

  /* ======== Data transformation ======== */
  const observedSegmentLabels = Array.from(
    new Set(dataIndexed.flatMap((bar) => Object.keys(bar.segments)))
  );
  const segmentLabels = (() => {
    if (segmentOrder === undefined) return observedSegmentLabels;
    const orderedKnown = segmentOrder.filter((label) =>
      observedSegmentLabels.includes(label)
    );
    const extras = observedSegmentLabels.filter(
      (label) => !segmentOrder.includes(label)
    );
    return [...orderedKnown, ...extras];
  })();
  const emptyBarsIndexed = dataIndexed.filter((bar) =>
    Object.values(bar.segments).every((value) => value === 0)
  );
  const emptyBarPatternId = 'diagonal-stripe-4';
  const stackedData = stack<StackedBarDataIndexed>()
    .keys(segmentLabels)
    .value((d, key) => {
      const total = Object.values(d.segments).reduce((sum, v) => sum + v, 0);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore noUncheckedIndexedAccess
      return d.segments[key] / total || 0;
    })(dataIndexed);

  /* Scales */
  const xScale = scaleLinear().domain([0, 1]).range([0, plotWidth]);
  const yScale = scaleBand(yDomain, [0, plotHeight])
    .paddingOuter(Dimensions.BarYSpacingFactor)
    .paddingInner(Dimensions.BarYSpacingFactor);

  const barHeight = yScale.bandwidth();

  const overridenColors = Object.values(colorOverride ?? {});
  const colors = colorPalette.filter(
    (color) => !overridenColors.includes(color)
  );
  const labelColors = segmentLabels.map((label, index) => ({
    label,
    color: colorOverride?.[label] ?? colors[index],
  }));

  const colorScale = scaleOrdinal<string>()
    .domain(labelColors.map(({ label }) => label))
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore noUncheckedIndexedAccess
    .range(labelColors.map(({ color }) => color))
    .unknown(getOtherColor());

  const formatMain = getFormatter(format);
  const formatSecondary = getFormatter(FormatType.PERCENTAGE);

  const renderBarSegment = ({
    series,
    segment,
  }: {
    series: d3.Series<StackedBarDataIndexed, string>;
    segment: SeriesPoint<StackedBarDataIndexed>;
  }) => {
    const x = xScale(segment[0]);
    const y = yScale(segment.data._index.toString());

    if (!y) return null;

    const width = Math.max(
      0,
      xScale(segment[1]) - xScale(segment[0]) - Dimensions.BarXSpacing
    );
    const color = colorScale(series.key);
    const entries = series.map((s) => ({
      active: s.data.label === segment.data.label,
      label: s.data.label,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore noUncheckedIndexedAccess
      mainValue: formatMain(s.data.segments[series.key]),
      secondaryValue: formatSecondary(s[1] - s[0]),
    }));

    if (width === 0) {
      return null;
    }
    return (
      <g key={`segment-${series.key}-${segment.data._index}`}>
        <StackedBarTooltip title={series.key} entries={entries}>
          <g>
            <rect
              x={x}
              y={y}
              fill={color}
              width={width}
              height={barHeight}
              className={styles.segment}
              rx={2}
              cursor="pointer"
            />
          </g>
        </StackedBarTooltip>
      </g>
    );
  };

  const noData =
    !data ||
    data?.length === 0 ||
    data.every((metric) => Object.values(metric.segments).length === 0) ||
    data.every((metric) =>
      Object.values(metric.segments).every((value) => value === 0)
    );

  return (
    <div
      data-testid={plotTestId(ReplotChartNames.StackedBarChartHorizontal)}
      ref={containerRef}
      className={styles.container}
    >
      <PlotLoadWrapper loading={loading} noData={noData}>
        <div style={{ width: '100%' }}>
          <svg width="100%" height={plotHeight}>
            <defs>
              <PatternDiagonalStripe patternId={emptyBarPatternId} />
            </defs>
            <g id="y-axis">
              {isPlotSizeValid &&
                yDomain.map((domainKey, index) => {
                  const y = yScale(domainKey);
                  return (
                    y && (
                      <AxisLabel
                        testId="stacked-horizontal-bar-chart-y-axis-label"
                        key={`axis-${domainKey}-${index}`}
                        y={y + barHeight / 2}
                        width={labelWidth}
                        availableHeight={barHeight + 2}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore noUncheckedIndexedAccess
                        label={displayLabels[index]}
                      />
                    )
                  );
                })}
            </g>
            <g
              id="chart"
              transform={`translate(${labelWidth + Dimensions.BarLeftPadding}, 0)`}
            >
              {isPlotSizeValid && (
                <g className={styles.barContainer}>
                  {stackedData.map((series) => {
                    return (
                      <g
                        key={`series-${series.key}`}
                        fill={colorScale(series.key) ?? getOtherColor()}
                        className={styles.bar}
                      >
                        {series.map((segment) =>
                          renderBarSegment({ series, segment })
                        )}
                      </g>
                    );
                  })}
                  {emptyBarsIndexed.map((emptyBar) => {
                    return (
                      <g
                        key={`empty-bar-${emptyBar.label}-${emptyBar._index}`}
                        className={styles.bar}
                      >
                        <StackedBarTooltip
                          title={emptyBar.label}
                          entries={undefined}
                        >
                          <rect
                            x={0}
                            y={yScale(emptyBar._index.toString())}
                            width={plotWidth}
                            height={barHeight}
                            className={styles.emptyBar}
                            fill={`url(#${emptyBarPatternId}) #cbd5e0`}
                          />
                        </StackedBarTooltip>
                      </g>
                    );
                  })}
                </g>
              )}
            </g>
          </svg>
          {!shouldHideLegend && isPlotSizeValid && (
            <div
              className={styles.legendContainer}
              style={{
                marginLeft: `${labelWidth + Dimensions.BarLeftPadding}px`,
                maxHeight: Dimensions.LegendHeight,
              }}
            >
              <Legend
                items={segmentLabels.map((label) => ({
                  label,
                  color: colorScale(label),
                }))}
                formatLabel={combinedLegendFormatter}
              />
            </div>
          )}
        </div>
      </PlotLoadWrapper>
    </div>
  );
};
